import { ReactNode, useEffect, useState } from 'react';
import { ScoreDisclosure } from 'widgets/score-disclosure/score-disclosure';
import cls from './information-completeness.module.scss';
import {
  useCachedBusinessInsights,
  useCachedTargetAudience,
  useCachedGeographicFocus,
  useCachedBusinessName,
  useCachedBusinessAddress,
  useCachedSelectedProject,
  useTranslation,
} from 'hooks';
import { Skeleton } from 'shared/ui/skeleton/skeleton';
import { SkeletonList } from 'shared/ui/skeleton/skeleton-list';
import { ImprovementBanner } from 'shared/ui/improvement-banner/improvement-banner';
import { Drawer } from 'shared/ui/drawer/drawer';
import { ImpactDrawerContent } from 'widgets/technical-rules/components/impact-drawer-content/impact-drawer-content';
import { Card } from 'shared/ui/card/card';
import { preloadTranslations } from 'services/translationService';
import { RULES_INFO_COMPLETENESS_TRANSLATION_FILE } from 'shared/constants/rules-texts';
import { RulesPurpose } from 'shared/ui/rules-purpose/rules-purpose';
import { useRuleStats } from 'shared/hooks/useRulesStat';
import { ImprovementSectionHeader } from 'widgets/improvement-section-header/improvement-section-header';
import { UnitSlot } from 'shared/ui/unit-slot/unit-slot';
import { RunBlockKey, useReportProgress } from 'shared/hooks/useReportProgress';
import { getSectionStatus } from 'shared/helpers/runSectionStatus';
import { RUN_BLOCK_EXPECTED_SECONDS } from 'shared/helpers/reportTimeline';

export interface InformationCompletenessProps {
  showBanner?: boolean;
  useImprovementVersion?: boolean;
}

export const InformationCompleteness: React.FC<
  InformationCompletenessProps
> = ({ showBanner, useImprovementVersion = false }) => {
  const { t, currentLocale } = useTranslation();
  const { reportGenerating, selectedProject, isTrialExpired } =
    useCachedSelectedProject();
  const { insights, isLoaded, isLoading } = useCachedBusinessInsights();
  const [isImpactDrawerOpen, setIsImpactDrawerOpen] = useState(false);
  const { completenessStat, completenessLoading } = useRuleStats();

  useEffect(() => {
    void preloadTranslations(currentLocale, [
      RULES_INFO_COMPLETENESS_TRANSLATION_FILE,
    ]);
  }, [currentLocale]);

  const {
    companyConfidence: targetConfidence,
    companyEvidance: targetEvidence,
    scoring: targetScoring,
    score: targetScore,
    isLoading: isLoadingAudience,
    isLoaded: audienceLoaded,
    identified: targetIdentified,
    urlsAnalyzed: targetUrls,
    extraction: targetExtraction,
  } = useCachedTargetAudience();

  const {
    companyConfidence: geoConfidence,
    companyEvidance: geoEvidence,
    score: geoScore,
    scoring: geoScoring,
    isLoading: isLoadingFocus,
    isLoaded: focusLoaded,
    identified: geoIdentified,
    urlsAnalyzed: geoUrls,
    extraction: geoExtraction,
  } = useCachedGeographicFocus();

  const {
    score: nameScore,
    scoring: nameScoring,
    companyConfidence: nameConfidence,
    companyEvidance: nameEvidence,
    isLoading: isLoadingName,
    isLoaded: nameLoaded,
    identified: nameIdentified,
    urlsAnalyzed: nameUrls,
    extraction: nameExtraction,
  } = useCachedBusinessName();

  const {
    score: addressScore,
    scoring: addressScoring,
    companyConfidence: addressConfidence,
    companyEvidance: addressEvidence,
    isLoading: isLoadingAddress,
    isLoaded: addressLoaded,
    identified: addressIdentified,
    urlsAnalyzed: addressUrls,
    extraction: addressExtraction,
  } = useCachedBusinessAddress();

  const { blocks, blockStartedAt, isRunActive, allComplete } =
    useReportProgress();
  const attributesLoading = reportGenerating ? !isLoaded : isLoading;

  const renderAttribute = (
    block: RunBlockKey,
    labelKey: string,
    fallbackHeight: number,
    content: ReactNode,
    fallbackClassName?: string
  ) => {
    if (isRunActive) {
      return (
        <UnitSlot
          unitState={blocks[block]}
          name={t(labelKey)}
          minHeight={76}
          className={useImprovementVersion ? cls.attributeSlot : undefined}
          startedAt={blockStartedAt[block]}
          expectedSeconds={RUN_BLOCK_EXPECTED_SECONDS[block]}
        >
          {content}
        </UnitSlot>
      );
    }

    if (!attributesLoading) {
      return content;
    }

    return fallbackClassName ? (
      <div className={fallbackClassName}>
        <Skeleton height={fallbackHeight} />
      </div>
    ) : (
      <Skeleton height={fallbackHeight} />
    );
  };

  if (!selectedProject) {
    return (
      <>
        <SkeletonList count={4} height={132} />
      </>
    );
  }

  if (!useImprovementVersion) {
    return (
      <div className={cls.info}>
        {renderAttribute(
          'businessName',
          'company_name',
          76,
          <ScoreDisclosure
            score={nameScore || 0}
            title={'company_name'}
            description={insights?.business_profile?.business_name ?? 'No name'}
            scoring={nameScoring}
            confidence={nameConfidence}
            evidence={nameEvidence}
            identified={nameIdentified}
            extraction={nameExtraction}
            urlsAnalyzed={nameUrls}
            isLoading={
              !isRunActive && (reportGenerating ? !nameLoaded : isLoadingName)
            }
          />
        )}
        {renderAttribute(
          'businessAddress',
          'business_address',
          76,
          <ScoreDisclosure
            score={addressScore || 0}
            title={'business_address'}
            description={
              insights?.business_profile?.business_address ?? 'No address'
            }
            scoring={addressScoring}
            confidence={addressConfidence}
            evidence={addressEvidence}
            identified={addressIdentified}
            urlsAnalyzed={addressUrls}
            extraction={addressExtraction}
            isLoading={
              !isRunActive &&
              (reportGenerating ? !addressLoaded : isLoadingAddress)
            }
          />
        )}
        {renderAttribute(
          'targetAudience',
          'target_audience',
          76,
          <ScoreDisclosure
            score={targetScore || 0}
            title={'target_audience'}
            description={
              insights?.business_profile?.target_audience ?? 'No name'
            }
            scoring={targetScoring}
            confidence={targetConfidence}
            evidence={targetEvidence}
            identified={targetIdentified}
            urlsAnalyzed={targetUrls}
            extraction={targetExtraction}
            isLoading={
              !isRunActive &&
              (reportGenerating ? !audienceLoaded : isLoadingAudience)
            }
          />
        )}
        {renderAttribute(
          'geographicFocus',
          'geographic_focus',
          76,
          <ScoreDisclosure
            score={geoScore || 0}
            title={'geographic_focus'}
            description={
              insights?.business_profile?.geographic_focus ?? 'No focus'
            }
            scoring={geoScoring}
            confidence={geoConfidence}
            evidence={geoEvidence}
            identified={geoIdentified}
            urlsAnalyzed={geoUrls}
            extraction={geoExtraction}
            isLoading={
              !isRunActive && (reportGenerating ? !focusLoaded : isLoadingFocus)
            }
          />
        )}
        {showBanner && blocks.contentFreshness === 'ready' && (
          <ImprovementBanner />
        )}
      </div>
    );
  }

  return (
    <Card className={cls.completenessCard}>
      <ImprovementSectionHeader
        title={t('businessInfoCompleteness')}
        stats={completenessStat}
        isLoading={isLoading || completenessLoading}
        isTrialExpired={isTrialExpired}
        runStatus={
          isRunActive
            ? getSectionStatus([
                blocks.businessName,
                blocks.businessAddress,
                blocks.targetAudience,
                blocks.geographicFocus,
              ])
            : null
        }
      />

      <div className={cls.completenessContainer}>
        <div className={cls.rulesSection}>
          <div className={cls.rulesList}>
            {renderAttribute(
              'businessName',
              'company_name',
              44,
              <ScoreDisclosure
                score={nameScore || 0}
                title={'company_name'}
                description={
                  insights?.business_profile?.business_name ?? 'No name'
                }
                scoring={nameScoring}
                confidence={nameConfidence}
                evidence={nameEvidence}
                identified={nameIdentified}
                extraction={nameExtraction}
                urlsAnalyzed={nameUrls}
                isLoading={
                  !isRunActive &&
                  (reportGenerating ? !nameLoaded : isLoadingName)
                }
                className={cls.disclosureItem}
                buttonClassName={cls.disclosureButton}
                contentClassName={cls.disclosureContent}
              />,
              cls.skeletonWrapper
            )}
            {renderAttribute(
              'businessAddress',
              'business_address',
              44,
              <ScoreDisclosure
                score={addressScore || 0}
                title={'business_address'}
                description={
                  insights?.business_profile?.business_address ?? 'No address'
                }
                scoring={addressScoring}
                confidence={addressConfidence}
                evidence={addressEvidence}
                identified={addressIdentified}
                urlsAnalyzed={addressUrls}
                extraction={addressExtraction}
                isLoading={
                  !isRunActive &&
                  (reportGenerating ? !addressLoaded : isLoadingAddress)
                }
                className={cls.disclosureItem}
                buttonClassName={cls.disclosureButton}
                contentClassName={cls.disclosureContent}
              />,
              cls.skeletonWrapper
            )}
            {renderAttribute(
              'targetAudience',
              'target_audience',
              44,
              <ScoreDisclosure
                score={targetScore || 0}
                title={'target_audience'}
                description={
                  insights?.business_profile?.target_audience ?? 'No name'
                }
                scoring={targetScoring}
                confidence={targetConfidence}
                evidence={targetEvidence}
                identified={targetIdentified}
                urlsAnalyzed={targetUrls}
                extraction={targetExtraction}
                isLoading={
                  !isRunActive &&
                  (reportGenerating ? !audienceLoaded : isLoadingAudience)
                }
                className={cls.disclosureItem}
                buttonClassName={cls.disclosureButton}
                contentClassName={cls.disclosureContent}
              />,
              cls.skeletonWrapper
            )}
            {renderAttribute(
              'geographicFocus',
              'geographic_focus',
              44,
              <ScoreDisclosure
                score={geoScore || 0}
                title={'geographic_focus'}
                description={
                  insights?.business_profile?.geographic_focus ?? 'No focus'
                }
                scoring={geoScoring}
                confidence={geoConfidence}
                evidence={geoEvidence}
                identified={geoIdentified}
                urlsAnalyzed={geoUrls}
                extraction={geoExtraction}
                isLoading={
                  !isRunActive &&
                  (reportGenerating ? !focusLoaded : isLoadingFocus)
                }
                className={cls.disclosureItem}
                buttonClassName={cls.disclosureButton}
                contentClassName={cls.disclosureContent}
              />,
              cls.skeletonWrapper
            )}
          </div>
          {showBanner && blocks.contentFreshness === 'ready' && (
            <ImprovementBanner />
          )}
        </div>

        <div className={cls.purposeSection}>
          <RulesPurpose
            purposeText={t('infoCompletenessDescription')}
            implementedText={t('infoCompletenessImplemented')}
            notImplementedText={t('infoCompletenessNotImplemented')}
            onViewFullImpact={() => setIsImpactDrawerOpen(true)}
            showViewFullImpact={!isRunActive || allComplete}
          />
        </div>
      </div>

      <Drawer
        isOpen={isImpactDrawerOpen}
        onClose={() => setIsImpactDrawerOpen(false)}
        title={`${t('businessInfoCompleteness')} ${t('fullImpact')}`}
      >
        <ImpactDrawerContent />
      </Drawer>
    </Card>
  );
};
